import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import SiteNav from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import PageHero from "@/components/site/PageHero";
import BottomCTA from "@/components/site/BottomCTA";
import useReveal from "@/hooks/useReveal";
import { FEATURED_ARTICLES } from "@/data/articles";
import { pickLocale } from "@/i18n";

export default function Insights() {
  useReveal();
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-white text-unik-ink">
      <SiteNav />

      <PageHero
        eyebrow={t("insights.heroEyebrow")}
        title={t("insights.heroTitle")}
        accent={t("insights.heroAccent")}
        sub={t("insights.heroSub")}
        bgImage="/assets/unsplash-1583417319070.jpg"
      />

      {/* FEATURED — 3-column hero grid */}
      <section className="relative bg-white py-28 lg:py-36">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div data-reveal className="mb-14 lg:mb-20 flex items-end justify-between flex-wrap gap-6">
            <div>
              <div className="eyebrow text-[#14355a]">{t("insights.featuredEyebrow")}</div>
              <div className="mt-6 w-12 h-px bg-unik-gold" />
              <h2
                style={{
                  fontSize: "clamp(2.25rem, 4.5vw, 4rem)",
                  fontWeight: 500,
                  lineHeight: "1.0",
                }}
                className="mt-8 font-display text-[#14355a] tracking-tight"
              >
                {t("insights.latestEyebrow")}
              </h2>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
            {FEATURED_ARTICLES.map((a, i) => (
              <Link
                key={a.slug}
                to={`/insights/${a.slug}`}
                data-testid={`featured-article-${a.slug}`}
                data-reveal
                style={{ "--reveal-delay": `${i * 130}ms` }}
                className="group block"
              >
                <div className="relative aspect-[4/5] overflow-hidden bg-[#0e2540]">
                  <img
                    src={a.img}
                    alt={pickLocale(a, "title")}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1800ms] group-hover:scale-[1.06]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0e2540]/95 via-[#0e2540]/30 to-transparent" />
                  <div className="absolute top-6 left-6">
                    <span className="inline-block px-3.5 py-1.5 bg-white/95 backdrop-blur-sm text-[#14355a] text-[10.5px] uppercase tracking-[0.22em] font-medium">
                      {pickLocale(a, "category") || a.category}
                    </span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-7 lg:p-8 text-white">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-white/65 font-medium">
                      {pickLocale(a, "read") || a.read}
                    </div>
                    <h3
                      style={{
                        fontSize: "clamp(1.375rem, 1.65vw, 1.625rem)",
                        fontWeight: 500,
                        lineHeight: 1.2,
                      }}
                      className="mt-4 font-display tracking-tight"
                    >
                      {pickLocale(a, "title")}
                    </h3>
                    <div className="mt-5 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-white/90 font-display">
                      {t("insights.readArticle")}
                      <ArrowRight
                        size={14}
                        className="transition-transform duration-500 group-hover:translate-x-1"
                      />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <BottomCTA
        eyebrow={t("insights.bottomCta.eyebrow")}
        title={t("insights.bottomCta.title")}
        accent={t("insights.bottomCta.accent")}
        body={t("insights.bottomCta.body")}
        primary={{ label: t("insights.bottomCta.primary"), to: "/contact" }}
        secondary={{ label: t("nav.about"), to: "/about/who-we-are" }}
      />

      <SiteFooter />
    </div>
  );
}
